// La foto de un mensaje, en grande — ADR-2026-09-14-la-foto-del-mensaje-se-vuelve-a-adjuntar.
//
// Se abre al tocar una miniatura de la burbuja del usuario (`AdjuntosDelUsuario`) y tiene
// UN solo botón: "Agregar al chat", que vuelve a enganchar esa foto al composer. Es la
// vuelta que faltaba: el usuario ya había mandado la foto, la ve tres mensajes más arriba,
// y hasta ahora la única forma de usarla de nuevo era buscarla en su compu y subirla otra
// vez.
//
// Sin descargar, sin editar, sin flechas para pasar de una a otra: la acción que importa en
// un chat es seguir la conversación con esa foto. Las otras ya tienen su lugar (Creaciones,
// el editor) y sumarlas acá convertiría un gesto de un toque en un menú.
//
// PORTAL A BODY, no un `fixed` adentro del chat: en esta app un `fixed` montado dentro de
// una página se mete debajo de la sidebar y del panel de contenido
// (`project_fixed_panel_stacking_trap`). Los z son los de `HojaLateral` —130 el fondo, 131
// el contenido— que es el overlay que ya convive con el panel del chat (`z-[110]` en
// mobile).
//
// ES UN DIÁLOGO MODAL Y SE COMPORTA COMO UNO
// (ADR-2026-09-15-el-visor-de-adjunto-maneja-el-foco). Decía `aria-modal` sin hacer nada
// por el foco, y eso fallaba en tres lugares a la vez: el foco se quedaba en la miniatura
// de atrás, el tabulador paseaba por la conversación que el visor tapa, y el fondo —que era
// un `button` a pantalla completa— era una parada de tabulación de más que hacía lo mismo
// que la ×.
//
// Y un detalle que sólo pasa acá: aunque el portal cuelgue de `body`, en el árbol de React
// este visor está DENTRO del panel del chat, que en mobile es otro diálogo con su propia
// trampa de foco y su propio Escape. Los eventos de teclado suben por el árbol de React, no
// por el DOM, así que sin frenarlos un Escape cerraba el visor Y el panel entero, y un Tab
// se lo comía la trampa del panel. Por eso el handler corta la propagación.

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';

/** Lo que puede recibir foco adentro del visor. La misma lista que la trampa del panel
 *  del chat (`PanelChatAtlas`): los controles que existen de verdad, nada exótico. */
const SELECTOR_ENFOCABLES =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const VisorDeAdjunto: React.FC<{
  url: string;
  /** Para el `alt` y el título: "Pose: de perfil", "Remera negra". */
  nombre: string;
  onCerrar: () => void;
  /** Ausente ⇒ el visor es sólo para mirar. Hoy no pasa: `adjuntoEsFoto` deja fuera de la
   *  miniatura tocable todo lo que no se puede volver a enganchar. */
  onAgregar?: () => void;
}> = ({ url, nombre, onCerrar, onAgregar }) => {
  const dialogoRef = useRef<HTMLDivElement>(null);
  const focoPrevioRef = useRef<HTMLElement | null>(null);

  // El foco ENTRA al diálogo y VUELVE a donde estaba (la miniatura que lo abrió). Entra al
  // contenedor y no a un botón: así el lector de pantalla anuncia el diálogo y su nombre
  // antes que "Agregar al chat", y el primer Tab cae igual en el primer control.
  useEffect(() => {
    focoPrevioRef.current = (document.activeElement as HTMLElement) ?? null;
    dialogoRef.current?.focus();
    return () => {
      focoPrevioRef.current?.focus?.();
    };
  }, []);

  // Escape con el foco FUERA del diálogo (se tocó el fondo, que no es enfocable, y el foco
  // se fue a `body`): ahí el handler de React no corre y éste sí. Con el foco adentro no se
  // duplica — el de abajo corta la propagación del evento nativo antes de llegar acá.
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclado);
    return () => document.removeEventListener('keydown', alTeclado);
  }, [onCerrar]);

  const alTeclado = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Escape') {
      // Frena antes de llegar al panel del chat, que cerraría el panel entero.
      ev.stopPropagation();
      onCerrar();
      return;
    }
    if (ev.key !== 'Tab') return;
    ev.stopPropagation();
    const raiz = dialogoRef.current;
    if (!raiz) return;
    const focos = Array.from(raiz.querySelectorAll<HTMLElement>(SELECTOR_ENFOCABLES)).filter(
      (el) => el.getClientRects().length > 0,
    );
    if (focos.length === 0) {
      ev.preventDefault();
      return;
    }
    const primero = focos[0];
    const ultimo = focos[focos.length - 1];
    const activo = document.activeElement as HTMLElement | null;
    // Con el foco en el contenedor (recién abierto) `contains` da true y no es ni el
    // primero ni el último: el Tab sigue su curso y cae solo en el primer control.
    if (ev.shiftKey && (activo === primero || !raiz.contains(activo))) {
      ev.preventDefault();
      ultimo.focus();
    } else if (!ev.shiftKey && (activo === ultimo || !raiz.contains(activo))) {
      ev.preventDefault();
      primero.focus();
    }
  };

  return createPortal(
    <>
      {/* El fondo cierra al tocarlo, pero NO es una parada de tabulación ni se anuncia: la
          × de arriba hace exactamente lo mismo y con el teclado alcanza una sola. */}
      <div
        aria-hidden="true"
        onClick={onCerrar}
        className="fixed inset-0 z-[130] bg-black/70 animate-fade-in"
      />
      <div
        ref={dialogoRef}
        role="dialog"
        aria-modal="true"
        aria-label={nombre}
        tabIndex={-1}
        onKeyDown={alTeclado}
        // `pointer-events-none` en el contenedor y `auto` en lo que se toca: así el aire
        // alrededor de la foto sigue siendo el fondo que cierra.
        className="pointer-events-none fixed inset-0 z-[131] flex flex-col items-center justify-center gap-4 p-4 outline-none"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
        }}
      >
        <CachedImg
          src={url}
          alt={nombre}
          // `min-h-0` para que el flex la deje achicarse: sin eso, una foto alta se pasa
          // del alto de la ventana y el botón queda abajo, fuera de pantalla.
          className="pointer-events-auto min-h-0 max-w-full flex-1 rounded-2xl object-contain shadow-2xl"
        />
        {onAgregar && (
          <button
            type="button"
            onClick={onAgregar}
            className="pointer-events-auto inline-flex shrink-0 items-center gap-2 rounded-full bg-white py-3 px-6 text-[15px] font-semibold text-brand-black shadow-lg transition-transform active:scale-[0.98]"
          >
            <Plus className="h-4.5 w-4.5" strokeWidth={2.5} aria-hidden="true" />
            Agregar al chat
          </button>
        )}
        {/* La X arriba a la derecha, por encima de todo: en mobile el fondo casi no se ve
            (la foto ocupa el ancho) y sin ella no habría cómo salir con el dedo. Va DENTRO
            del diálogo para que la trampa de foco la cuente. */}
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="pointer-events-auto fixed right-4 z-[132] inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
        >
          <X className="h-5 w-5" strokeWidth={2.25} />
        </button>
      </div>
    </>,
    document.body,
  );
};

export default VisorDeAdjunto;
